import {
  ConditionFieldEnum,
  getRelationshipDirection,
  MAX_RELATED_RESOURCE_DEPTH,
  type BusinessSegmentConditions,
  type SegmentConditionGroups,
} from '@reach/shared-types/conditions';
import {
  assembleSegmentSchemaCatalog,
  buildSchemaRelationshipGraph,
  fieldNameFromJsonPath,
  getRelationshipOptionsForSubject,
} from '@reach/shared-types/segment-schema-graph';
import {
  PartnerSegmentGenerationStatus,
  type PartnerSegmentGenerationEnqueuedResponse,
  type PartnerSegmentGenerationStatusResponse,
  type TextToBusinessSegmentResponseBody,
} from '@reach/shared-types/business-segments';
import { partnerApi, type ApiResponse } from '../../../lib/api';
import { API_ENDPOINTS } from '../../../lib/constants';
import type {
  AvailableFieldsResponse,
  RelatedSchema,
  SegmentSchemaCatalog,
  SchemaDefinitionWithFields,
  SchemaRelationshipGraph,
} from './types';
export interface CreateSegmentDefinitionResponse {
  id: string;
  canonicalVersionId: string;
}

export interface CreateSegmentDefinitionParams {
  name: string;
  description: string;
  contents?: BusinessSegmentConditions;
  conditionGroups?: SegmentConditionGroups;
  targetedTenantsFilter?: string[] | null;
}

export interface GetSegmentDefinitionResponse {
  id: string;
  name: string;
  description: string | null;
  canonicalVersionId: string;
  contents: BusinessSegmentConditions;
  conditionGroups?: SegmentConditionGroups;
  updatedAt: string | null;
  targetedTenantsFilter: string[] | null;
  /** Business IDs that have this segment in an active automation; partner cannot deselect these tenants until automations are turned off. */
  businessIdsWithSegmentInActiveAutomation: string[];
  /** For each business ID, the names of automations that use this segment (for conflict messaging). */
  conflictingAutomationNamesByBusinessId: Record<string, string[]>;
}

/**
 * Legacy format = conditions (contents) exist AND condition_groups is empty or not provided.
 * Use this everywhere we need to decide between legacy (conditions) vs new (conditionGroups).
 */
export function isLegacySegmentFormat(args: {
  conditions?: BusinessSegmentConditions | null;
  conditionGroups?: SegmentConditionGroups | null;
}): boolean {
  return (
    args.conditions !== undefined &&
    args.conditions !== null &&
    (!args.conditionGroups || args.conditionGroups.length === 0)
  );
}

export interface UpdateSegmentDefinitionParams {
  name?: string;
  description?: string;
  contents?: BusinessSegmentConditions;
  conditionGroups?: SegmentConditionGroups | null;
  targetedTenantsFilter?: string[] | null;
}

export interface UpdateSegmentDefinitionResponse {
  id: string;
}

/**
 * Create a new segment definition.
 * platformId is automatically derived from auth context.
 */
export async function createSegmentDefinitionFromTemplate(
  params: CreateSegmentDefinitionParams,
): Promise<CreateSegmentDefinitionResponse> {
  const body: Record<string, unknown> = {
    name: params.name,
    description: params.description,
    contents: params.contents ?? [],
    conditionGroups: params.conditionGroups,
  };
  if (params.targetedTenantsFilter !== undefined) {
    body.targetedTenantsFilter = params.targetedTenantsFilter;
  }

  const response = await partnerApi.request<ApiResponse<CreateSegmentDefinitionResponse>>(
    API_ENDPOINTS.PARTNER.SEGMENT_DEFINITIONS,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  );

  if (!response.success) {
    throw new Error(response.message || 'Failed to create segment');
  }

  return response.data;
}

const SEGMENT_GENERATION_POLL_INTERVAL_MS = 2_000;
/**
 * Generation runs in a background processor (multiple model attempts, each up
 * to 90s). A job that dead-letters stays "processing" forever, so the poll
 * loop needs its own ceiling; past it we report a generic failure.
 */
const SEGMENT_GENERATION_POLL_TIMEOUT_MS = 6 * 60 * 1000;
const API_ERROR_RESULT: TextToBusinessSegmentResponseBody = {
  success: false,
  failureReason: 'api_error',
};

function sleep(durationMs: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, durationMs));
}

/**
 * Generate a segment definition draft from a plain-language description.
 * Enqueues the generation, then polls the job until it completes. The resolved
 * value reports failure via the `success` flag — including a description too
 * vague to compile — rather than by rejecting.
 * Called from DescribeSegmentModal (the "Describe your segment" AI entry point).
 */
export async function textToPartnerSegment(text: string): Promise<TextToBusinessSegmentResponseBody> {
  const enqueueResponse = await partnerApi.request<
    ApiResponse<PartnerSegmentGenerationEnqueuedResponse>
  >(API_ENDPOINTS.PARTNER.SEGMENT_TEXT_TO_SEGMENT, {
    method: 'POST',
    body: JSON.stringify({ text }),
  });
  const { jobId } = enqueueResponse.data;

  const pollDeadline = Date.now() + SEGMENT_GENERATION_POLL_TIMEOUT_MS;
  while (Date.now() < pollDeadline) {
    await sleep(SEGMENT_GENERATION_POLL_INTERVAL_MS);
    const statusResponse = await partnerApi.request<ApiResponse<PartnerSegmentGenerationStatusResponse>>(
      `${API_ENDPOINTS.PARTNER.SEGMENT_TEXT_TO_SEGMENT}/${jobId}`,
    );
    const generation = statusResponse.data;

    if (generation.status === PartnerSegmentGenerationStatus.COMPLETED) {
      return generation.result ?? API_ERROR_RESULT;
    }
    if (generation.status === PartnerSegmentGenerationStatus.FAILED) {
      return API_ERROR_RESULT;
    }
  }

  return API_ERROR_RESULT;
}

/** Get a segment definition by canonical version ID. */
export async function getSegmentDefinition(
  canonicalVersionId: string,
): Promise<GetSegmentDefinitionResponse> {
  const response = await partnerApi.request<ApiResponse<GetSegmentDefinitionResponse>>(
    `${API_ENDPOINTS.PARTNER.SEGMENT_DEFINITIONS}/${canonicalVersionId}`,
  );

  return response.data;
}

/** Update a segment definition. */
export async function updateSegmentDefinition(
  canonicalVersionId: string,
  params: UpdateSegmentDefinitionParams,
): Promise<UpdateSegmentDefinitionResponse> {
  const response = await partnerApi.request<ApiResponse<UpdateSegmentDefinitionResponse>>(
    `${API_ENDPOINTS.PARTNER.SEGMENT_DEFINITIONS}/${canonicalVersionId}`,
    {
      method: 'PUT',
      body: JSON.stringify(params),
    },
  );

  if (!response.success) {
    throw new Error(response.message || 'Failed to update segment');
  }

  return response.data;
}

/**
 * Default built-in fields that are always available for segment conditions
 * Derived from ConditionFieldEnum to stay in sync with backend
 */
const DEFAULT_FIELDS = ConditionFieldEnum.options;

function findPrimaryContactSchema(
  schemas: SchemaDefinitionWithFields[],
  primaryContactSchemaId: string | undefined,
): SchemaDefinitionWithFields | undefined {
  return schemas.find(schema => schema.id === primaryContactSchemaId);
}

/**
 * Get field names from schema's fields_to_expose (using jsonPath).
 * Returns empty array if fields_to_expose is empty/null (no fallback).
 */
function getSchemaFieldNames(schema: SchemaDefinitionWithFields): string[] {
  const expose = schema.fields_to_expose ?? schema.fieldsToExpose;
  if (!expose || !Array.isArray(expose)) return [];

  const fields: string[] = [];
  for (const f of expose) {
    const fieldName = fieldNameFromJsonPath(f.jsonPath);
    if (fieldName) fields.push(fieldName);
  }
  return fields;
}

/**
 * Get field names from schema's fields_to_expose (explicit name field).
 * For use with legacy format (contents) only. Returns empty array if empty/null.
 */
function getSchemaFieldNamesLegacy(schema: SchemaDefinitionWithFields): string[] {
  const expose = schema.fields_to_expose ?? schema.fieldsToExpose;
  if (!expose || !Array.isArray(expose)) return [];

  const fields: string[] = [];
  for (const f of expose) {
    if (f.name && typeof f.name === 'string') fields.push(f.name);
  }
  return fields;
}

function getRelatedSchemasFromGraph(
  schemaDefinitions: SchemaDefinitionWithFields[],
  graph: SchemaRelationshipGraph,
): RelatedSchema[] {
  const contactSchemaId = graph.contactSchemaId;
  if (!contactSchemaId) return [];

  return getRelationshipOptionsForSubject({
    graph,
    subjectSchemaId: contactSchemaId,
  }).flatMap(relationship => {
    const { relatedSchemaId } = getRelationshipDirection({
      relationship,
      subjectSchemaId: contactSchemaId,
    });
    const relatedSchema = schemaDefinitions.find(schema => schema.id === relatedSchemaId);
    if (!relatedSchema) return [];

    const fields = getSchemaFieldNames(relatedSchema);
    const fieldsLegacy = getSchemaFieldNamesLegacy(relatedSchema);
    return [
      {
        id: relatedSchema.id,
        name: relatedSchema.name,
        foreignKeyPath: relationship.foreignKeyPath,
        fields: [...fields].sort(),
        fieldsLegacy: fieldsLegacy.length > 0 ? [...fieldsLegacy].sort() : undefined,
      },
    ];
  });
}

/**
 * Every schema a condition group can reach by walking relationship hops from the
 * contact, up to MAX_RELATED_RESOURCE_DEPTH. Hop 1 is contact-adjacent; deeper
 * hops follow the same subject rules the builder uses, so the set matches exactly
 * what a nested condition can target.
 */
function collectReachableSchemaIds(graph: SchemaRelationshipGraph): Set<string> {
  const { contactSchemaId } = graph;
  const reachableSchemaIds = new Set<string>();
  if (!contactSchemaId) return reachableSchemaIds;

  let subjectSchemaIds = [contactSchemaId];
  for (let depth = 1; depth <= MAX_RELATED_RESOURCE_DEPTH; depth++) {
    const nextSubjectSchemaIds: string[] = [];
    for (const subjectSchemaId of subjectSchemaIds) {
      for (const relationship of getRelationshipOptionsForSubject({ graph, subjectSchemaId })) {
        const { relatedSchemaId } = getRelationshipDirection({ relationship, subjectSchemaId });
        const alreadyCollected =
          relatedSchemaId === contactSchemaId || reachableSchemaIds.has(relatedSchemaId);
        if (alreadyCollected) continue;
        reachableSchemaIds.add(relatedSchemaId);
        nextSubjectSchemaIds.push(relatedSchemaId);
      }
    }
    subjectSchemaIds = nextSubjectSchemaIds;
  }

  return reachableSchemaIds;
}

/**
 * Field names on every schema reachable from the contact, in both naming formats.
 * Used to validate hand-written condition JSON, which can name a field on a schema
 * that no direct contact relationship exposes.
 */
function getReachableSchemaFieldNames(
  schemaDefinitions: SchemaDefinitionWithFields[],
  graph: SchemaRelationshipGraph,
): Pick<AvailableFieldsResponse, 'nestedFields' | 'nestedFieldsLegacy'> {
  const reachableSchemaIds = collectReachableSchemaIds(graph);
  const nestedFields = new Set<string>();
  const nestedFieldsLegacy = new Set<string>();

  for (const schemaDefinition of schemaDefinitions) {
    if (!reachableSchemaIds.has(schemaDefinition.id)) continue;
    for (const name of getSchemaFieldNames(schemaDefinition)) nestedFields.add(name);
    for (const name of getSchemaFieldNamesLegacy(schemaDefinition)) nestedFieldsLegacy.add(name);
  }

  return {
    nestedFields: [...nestedFields].sort(),
    nestedFieldsLegacy: [...nestedFieldsLegacy].sort(),
  };
}

/**
 * Fetch available fields from schema definitions.
 * Returns contact fields and related resource schemas that reference the contact schema
 * (for condition groups / cross-object segment conditions).
 */
export async function fetchAvailableFields(): Promise<AvailableFieldsResponse> {
  const [response, schemaMappings] = await Promise.all([
    partnerApi.request<ApiResponse<SchemaDefinitionWithFields[]>>(
      API_ENDPOINTS.PARTNER.SCHEMA_DEFINITIONS,
    ),
    partnerApi.getSchemaMappings(),
  ]);

  const schemas = response.data;
  const contactSchema = findPrimaryContactSchema(
    schemas,
    schemaMappings.contactsSchema?.primarySource?.schemaId,
  );
  const contactSchemaId = contactSchema?.id ?? null;

  const contactFields: string[] = [...DEFAULT_FIELDS];
  const contactFieldsLegacy: string[] = [...DEFAULT_FIELDS];
  if (contactSchema) {
    for (const name of getSchemaFieldNames(contactSchema)) {
      if (!contactFields.includes(name)) contactFields.push(name);
    }
    for (const name of getSchemaFieldNamesLegacy(contactSchema)) {
      if (!contactFieldsLegacy.includes(name)) contactFieldsLegacy.push(name);
    }
  }
  contactFields.sort();
  contactFieldsLegacy.sort();

  const relationshipGraph = buildSchemaRelationshipGraph(schemas, contactSchemaId);
  const relatedSchemas = getRelatedSchemasFromGraph(schemas, relationshipGraph);

  return {
    contactFields,
    contactFieldsLegacy,
    relatedSchemas,
    ...getReachableSchemaFieldNames(schemas, relationshipGraph),
  };
}

/**
 * Fetch available fields with types for the visual segment builder.
 * Returns contact fields plus the full schema relationship graph: every
 * relationship between every pair of schemas, in both directions, with the
 * foreign-key path named — one entry per referencing property.
 */
export async function fetchAvailableFieldsWithTypes(): Promise<SegmentSchemaCatalog> {
  const [response, schemaMappings] = await Promise.all([
    partnerApi.request<ApiResponse<SchemaDefinitionWithFields[]>>(
      API_ENDPOINTS.PARTNER.SCHEMA_DEFINITIONS,
    ),
    partnerApi.getSchemaMappings(),
  ]);

  const schemaDefinitions = response.data;
  const contactSchema = findPrimaryContactSchema(
    schemaDefinitions,
    schemaMappings.contactsSchema?.primarySource?.schemaId,
  );

  return assembleSegmentSchemaCatalog({
    schemaDefinitions,
    contactSchemaId: contactSchema?.id ?? null,
  });
}

export interface PreviewRecipient {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
}

export interface PreviewSegmentRecipientsResponse {
  recipients: PreviewRecipient[];
  pagination: {
    total: number;
    hasNextPage: boolean;
    cursor?: string;
  };
  estimatedTotal?: number;
}

/**
 * Preview segment recipients for a given tenant
 * Uses the tenant-scoped endpoint with reach-tenant-id header.
 * Supports both legacy (conditions) and new (conditionGroups) formats.
 */
export async function previewSegmentRecipients(
  tenantId: string,
  conditions?: BusinessSegmentConditions,
  conditionGroups?: SegmentConditionGroups,
  limit = 20,
  cursor?: string,
): Promise<PreviewSegmentRecipientsResponse> {
  const body: Record<string, unknown> = {
    limit,
    cursor,
  };

  if (isLegacySegmentFormat({ conditions, conditionGroups })) {
    body.conditions = conditions;
  } else if (conditionGroups !== undefined && conditionGroups !== null && conditionGroups.length > 0) {
    body.conditionGroups = conditionGroups;
  }

  const response = await partnerApi.request<ApiResponse<PreviewSegmentRecipientsResponse>>(
    API_ENDPOINTS.AUTOMATIONS.PREVIEW_SEGMENT_RECIPIENTS,
    {
      method: 'POST',
      headers: {
        'reach-tenant-id': tenantId,
      },
      body: JSON.stringify(body),
    },
  );

  return response.data;
}
